import * as Yup from 'yup'
import { cpf, cnpj } from 'cpf-cnpj-validator'

export const addEditAccountValidation = Yup.object().shape({
  nationalRegistration: Yup.string()
    .test('cpf-cnpj-validation', 'Insira um CPF ou CNPJ válido', (i) => {
      if (i !== undefined) {
        if (cpf.isValid(i) || cnpj.isValid(i)) {
          return true
        } else {
          return false
        }
      } else {
        return false
      }
    })
    .required('Requerido'),
  accountOwner: Yup.string()
    .min(2, 'Nome muito pequeno')
    .matches(
      /^[a-zA-Z -záàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ 0-9]+$/,
      'Insira apenas letras ou números'
    )
    .required('Requerido'),
  transactionType: Yup.string().required('Requerido'),
  bank: Yup.string().when('transactionType', {
    is: 'PIX',
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required('Requerido'),
  }),
  agency: Yup.string().when('transactionType', {
    is: 'PIX',
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema
        .matches(/^[0-9]+$/, 'Insira apenas números')
        .min(4, 'Poucos dígitos')
        .max(4, 'Muitos dígitos')
        .required('Requerido'),
  }),
  accountType: Yup.string().when('transactionType', {
    is: 'PIX',
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required('Requerido'),
  }),
  accountNumber: Yup.string().when('transactionType', {
    is: 'PIX',
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema
        .min(2, 'Poucos dígitos')
        .max(20, 'Muitos dígitos')
        .matches(/^[0-9A-Za-z]+$/, 'Insira o número da conta')
        .required('Requerido'),
  }),
  accountDigit: Yup.string().when('transactionType', {
    is: 'PIX',
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema.max(1, 'Apenas um dígito').required('Se não existir coloque 0'),
  }),
  accountIdentifier: Yup.string()
    .min(2, 'Nome muito pequeno')
    .matches(
      /^[a-zA-Z -záàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ 0-9]+$/,
      'Insira apenas letras ou números'
    )
    .required('Requerido'),
  pixKey: Yup.string().when(['transactionType', 'pixTypeKey'], {
    is: (transactionType: string, pixTypeKey: string) =>
      transactionType === 'PIX',
    then: (schema) =>
      schema
        .required('A chave PIX é obrigatória')
        .test('no-spaces', 'A chave PIX não pode conter espaços', (value) =>
          value ? !/\s/.test(value) : true
        )
        .test('pix-key-validation', 'Chave PIX inválida', function (value) {
          const { pixTypeKey } = this.parent

          if (!value) return false

          if (pixTypeKey === 'CPF') {
            return cpf.isValid(value)
          }

          if (pixTypeKey === 'CNPJ') {
            return cnpj.isValid(value)
          }

          if (pixTypeKey === 'E-mail') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            return emailRegex.test(value)
          }

          if (pixTypeKey === 'Número de telefone') {
            const phoneRegex =
              /^(?:\+55\s?)?(?:\(\d{2}\)\s?)?\d{4,5}-?\d{4}$|^\d{10,11}$/
            return phoneRegex.test(value)
          }

          if (pixTypeKey === 'Chave aleatória') {
            const uuidRegex =
              /^[0-9a-fA-F]{8}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{12}$/
            return uuidRegex.test(value)
          }

          return true
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  pixTypeKey: Yup.string().when('transactionType', {
    is: 'PIX',
    then: (schema) => schema.required('O tipo da chave PIX é obrigatório'),
    otherwise: (schema) => schema.notRequired(),
  }),
})

export const pixValidationSchema = Yup.object().shape({
  nationalRegistration: Yup.string()
    .test('cpf-cnpj-validation', 'Insira um CPF ou CNPJ válido', (i) => {
      if (i !== undefined) {
        if (cpf.isValid(i) || cnpj.isValid(i)) {
          return true
        } else {
          return false
        }
      } else {
        return false
      }
    })
    .required('Requerido'),
  accountOwner: Yup.string()
    .min(2, 'Nome muito pequeno')
    .matches(
      /^[a-zA-Z -záàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ 0-9]+$/,
      'Insira apenas letras ou números'
    )
    .required('Requerido'),
  transactionType: Yup.string().required('Tipo de transação obrigatório'),
  pixKey: Yup.string()
    .required('Chave PIX é obrigatória')
    .test('no-spaces', 'A chave PIX não pode conter espaços', (value) =>
      value ? !/\s/.test(value) : true
    )
    .test('pix-key-validation', 'Chave PIX inválida', function (value) {
      const { pixTypeKey } = this.parent

      if (!value) return false

      if (pixTypeKey === 'CPF') {
        return cpf.isValid(value)
      }

      if (pixTypeKey === 'CNPJ') {
        return cnpj.isValid(value)
      }

      if (pixTypeKey === 'E-mail') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(value)
      }

      if (pixTypeKey === 'Número de telefone') {
        const phoneRegex = /^(?:\+55\s?)?(?:\(\d{2}\)\s?)?\d{4,5}-?\d{4}$/
        return phoneRegex.test(value)
      }

      if (pixTypeKey === 'Chave aleatória') {
        const uuidRegex =
          /^[0-9a-fA-F]{8}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{12}$/
        return uuidRegex.test(value)
      }

      return true
    }),
  pixTypeKey: Yup.string().required('Tipo de chave PIX obrigatório'),
})

export const withdrawValueValidation = Yup.object().shape({
  withdrawMoney: Yup.number().required('Requerido'),
})

export const advanceSimulationValidation = Yup.object().shape({
  withdrawMoney: Yup.number().required('Requerido'),
})

export const descriptionAndTermsValidation = Yup.object().shape({
  termsAndConditions: Yup.bool().oneOf(
    [true],
    'You need to accept the terms and conditions'
  ),
})

export const getValidationSchema = (usePixForm: boolean) => {
  return Yup.object().shape({
    nationalRegistration: Yup.string()
      .test('cpf-cnpj-validation', 'Insira um CPF ou CNPJ válido', (i) => {
        if (i !== undefined) {
          if (cpf.isValid(i) || cnpj.isValid(i)) {
            return true
          } else {
            return false
          }
        } else {
          return false
        }
      })
      .required('Requerido'),
    accountOwner: Yup.string()
      .min(2, 'Nome muito pequeno')
      .matches(
        /^[a-zA-Z -záàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ 0-9]+$/,
        'Insira apenas letras ou números'
      )
      .required('Requerido'),
    transactionType: Yup.string().required('Requerido'),
    bank: Yup.string().when('transactionType', {
      is: 'PIX',
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required('Requerido'),
    }),
    agency: Yup.string().when('transactionType', {
      is: 'PIX',
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required('Requerido'),
    }),
    accountType: Yup.string().when('transactionType', {
      is: 'PIX',
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required('Requerido'),
    }),
    accountNumber: Yup.string().when('transactionType', {
      is: 'PIX',
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required('Requerido'),
    }),
    accountIdentifier: Yup.string().when('transactionType', {
      is: 'PIX',
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required('Requerido'),
    }),
    pixKey: Yup.string().when(['transactionType', 'pixTypeKey'], {
      is: (transactionType: string, pixTypeKey: string) =>
        transactionType === 'PIX',
      then: (schema) =>
        schema
          .required('A chave PIX é obrigatória')
          .test('no-spaces', 'A chave PIX não pode conter espaços', (value) =>
            value ? !/\s/.test(value) : true
          )
          .test('pix-key-validation', 'Chave PIX inválida', function (value) {
            const { pixTypeKey } = this.parent

            if (!value) return false

            if (pixTypeKey === 'CPF') {
              return cpf.isValid(value)
            }

            if (pixTypeKey === 'CNPJ') {
              return cnpj.isValid(value)
            }

            if (pixTypeKey === 'E-mail') {
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
              return emailRegex.test(value)
            }

            if (pixTypeKey === 'Número de telefone') {
              const phoneRegex = /^(\+55\s?)?[({]?\d{2}[)}]?\s?\d{4,5}-?\d{4}$/
              return phoneRegex.test(value)
            }

            if (pixTypeKey === 'Chave aleatória') {
              const uuidRegex =
                /^[0-9a-fA-F]{8}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{12}$/
              return uuidRegex.test(value)
            }

            return true
          }),
      otherwise: (schema) => schema.notRequired(),
    }),
    pixTypeKey: Yup.string().when('transactionType', {
      is: 'PIX',
      then: (schema) => schema.required('O tipo da chave PIX é obrigatório'),
      otherwise: (schema) => schema.notRequired(),
    }),
  })
}
